/**
 * Activity-feed data hooks.
 *
 * These power the Daily Brief's "Needs your attention" section
 * (useActivitiesTab) and the header bell's review-count badge
 * (useActivitiesBadge). The standalone Activities panel (Review/Upcoming/Past)
 * was replaced by the Daily Brief, but its underlying cron-delivered feed lives
 * on — that's what these read.
 */

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/keys';
import { useSession } from '@/features/auth/hooks/useSession';
import { http } from '@/lib/http';

export interface ActivityItem {
  id: string;
  type: string;
  title?: string;
  description?: string;
  status?: string;
  priority?: string;
  dueAt?: string;
  createdAt: string;
  updatedAt?: string;
  reviewCount?: number;
  objectId?: string;
  objectType?: string;
  verb?: string;
  conversationId?: string;
  source?: string;
  actions?: Array<{ label: string; action: string; params?: Record<string, unknown> }>;
  _kind?: string;
  [key: string]: unknown;
}

interface RawApiItem {
  [key: string]: unknown;
}

interface RawTabResponse {
  items: RawApiItem[];
  total: number;
  reviewCount?: number;
}

/** Normalize API snake_case response to camelCase ActivityItem */
function normalizeItem(raw: RawApiItem): ActivityItem {
  return {
    ...raw,
    id: String(raw.id ?? ''),
    // Title: use object_title (activity), title (reminder/task), or fallback
    title: String(raw.object_title ?? raw.title ?? raw.label ?? ''),
    // Description: use content (activity) or description (reminder), skip nulls
    description: raw.content != null ? String(raw.content) : raw.description != null ? String(raw.description) : '',
    type: String(raw.object_type ?? raw.type ?? raw._kind ?? ''),
    objectType: String(raw.object_type ?? raw.type ?? ''),
    objectId: raw.object_id != null ? String(raw.object_id) : undefined,
    status: String(raw.status ?? ''),
    priority: String(raw.priority ?? 'normal'),
    verb: raw.verb != null ? String(raw.verb) : undefined,
    conversationId: raw.conversation_id != null ? String(raw.conversation_id) : undefined,
    // Dates: map snake_case to camelCase
    createdAt: String(raw.created_at ?? raw.createdAt ?? ''),
    updatedAt: raw.updated_at != null ? String(raw.updated_at) : undefined,
    // For upcoming: use due_date (tasks), start_date (events), next_fire_at (reminders)
    dueAt: String(raw.due_date ?? raw.start_date ?? raw.next_fire_at ?? raw.next_run_at ?? raw.dueAt ?? raw.created_at ?? ''),
    // Actions: may be array (already parsed by API) or string
    actions: Array.isArray(raw.actions) ? raw.actions as ActivityItem['actions'] : undefined,
    _kind: raw._kind != null ? String(raw._kind) : undefined,
    source: raw.source != null ? String(raw.source) : undefined,
  } as ActivityItem;
}

export interface ActivitiesTabResponse {
  items: ActivityItem[];
  total: number;
  reviewCount?: number;
}

export interface ActivitiesBadgeResponse {
  reviewCount: number;
}

/**
 * Fetches activities for a specific tab in the activities panel.
 * GET /workspaces/:workspaceId/activities?tab=:tab&limit=50
 */
export function useActivitiesTab(
  workspaceId: string,
  tab: string,
  enabled: boolean
) {
  const { token } = useSession();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.activities.tab(workspaceId, tab),
    queryFn: async () => {
      const encodedWorkspaceId = encodeURIComponent(workspaceId);
      const response = await http.get<RawTabResponse>(
        `/workspaces/${encodedWorkspaceId}/activities?tab=${encodeURIComponent(tab)}&limit=50`,
        { authToken: token }
      );
      const raw = response.data;
      return {
        items: (raw.items ?? []).map(normalizeItem),
        total: raw.total ?? 0,
        reviewCount: raw.reviewCount ?? 0,
      } as ActivitiesTabResponse;
    },
    staleTime: 30000,
    enabled: enabled && !!token && !!workspaceId,
  });

  return {
    items: data?.items ?? [],
    total: data?.total ?? 0,
    reviewCount: data?.reviewCount ?? 0,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Polls the activities badge count (review/pending items).
 * GET /workspaces/:workspaceId/activities/badge
 */
export function useActivitiesBadge(workspaceId: string, enabled = true) {
  const { token } = useSession();

  const { data } = useQuery({
    queryKey: queryKeys.activities.badge(workspaceId),
    queryFn: async () => {
      const encodedWorkspaceId = encodeURIComponent(workspaceId);
      const response = await http.get<ActivitiesBadgeResponse>(
        `/workspaces/${encodedWorkspaceId}/activities/badge`,
        { authToken: token }
      );
      return response.data;
    },
    refetchInterval: 30000,
    enabled: enabled && !!token && !!workspaceId,
  });

  return {
    reviewCount: data?.reviewCount ?? 0,
  };
}
